<template>
    <section id="offer" class="ob-band offer">
        <div class="offer-shell">
            <div class="offer-head">
                <div class="offer-eyebrow">Offerings</div>
                <h2 class="offer-h2">Who are we connecting?</h2>
                <p class="offer-sub">Flip the switch — the whole section changes with you.</p>

                <div class="offer-toggle">
                    <button class="toggle-word" :class="{ active: !store.isCorp }" @click="store.setMode('retail')">Home</button>
                    <button
                        class="toggle-track"
                        :class="{ corp: store.isCorp }"
                        role="switch"
                        :aria-checked="store.isCorp"
                        aria-label="Switch between Home and Business"
                        @click="store.toggleMode"
                    >
                        <span class="toggle-knob" :class="{ corp: store.isCorp }" />
                    </button>
                    <button class="toggle-word" :class="{ active: store.isCorp }" @click="store.setMode('corp')">Business</button>
                </div>
                <div class="offer-band-label">{{ store.isCorp ? 'Showing One Business' : 'Showing Home' }}</div>
            </div>

            <div class="offer-title-block">
                <h3 class="offer-h3">{{ store.isCorp ? 'More than an internet line.' : 'Enough internet to just play.' }}</h3>
                <p class="offer-blurb">{{ store.isCorp
                    ? 'A turn-key partner, not just a provider. We design, install and run the whole setup — connection, network, security and continuity — under one contract.'
                    : 'Three tiers, no throttling, and your first router free. Pick the one that matches how your household uses the internet.' }}</p>
            </div>

            <div class="lane-r">
                <HomeLane />
            </div>
            <div class="lane-c">
                <BusinessLane />
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { useLandingStore } from '@/stores/landing'
    import BusinessLane from '@/views/landing/components/BusinessLane.vue'
    import HomeLane from '@/views/landing/components/HomeLane.vue'

    const store = useLandingStore()
</script>

<style scoped>
    .offer { padding: 88px 0 96px; }
    .offer-shell { max-width: 1240px; margin: 0 auto; padding: 0 44px; }
    .offer-head { text-align: center; max-width: 780px; margin: 0 auto; }
    .offer-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--eyebrow); }
    .offer-h2 {
        margin: 16px 0 0;
        font-size: clamp(30px, 3.8vw, 50px);
        font-weight: 800;
        letter-spacing: -.03em;
        line-height: 1.02;
        color: var(--ink);
    }
    .offer-sub { margin: 14px auto 0; max-width: 520px; font-size: 16.5px; line-height: 1.6; color: var(--sub); }

    .offer-toggle { display: flex; align-items: center; justify-content: center; gap: 26px; margin-top: 38px; }
    .toggle-word {
        cursor: pointer;
        border: none;
        background: none;
        padding: 0;
        font-family: 'Poppins', sans-serif;
        letter-spacing: -.02em;
        transition: all .25s;
        font-size: clamp(22px, 2.5vw, 30px);
        font-weight: 600;
        color: var(--ob-navy-a66);
    }
    .ob-corp .toggle-word { color: var(--ob-w-a72); }
    .toggle-word.active {
        font-size: clamp(26px, 3vw, 38px);
        font-weight: 800;
        color: var(--ink);
    }
    .toggle-track {
        cursor: pointer;
        position: relative;
        flex: none;
        width: 112px;
        height: 56px;
        border-radius: 999px;
        border: none;
        padding: 0;
        transition: background .35s ease, box-shadow .35s ease;
        background: var(--ob-grad-track);
        box-shadow: var(--ob-shadow-track);
    }
    .toggle-track.corp {
        background: var(--ob-grad-track-corp);
        box-shadow: var(--ob-shadow-track-corp);
    }
    .toggle-knob {
        position: absolute;
        top: 5px;
        left: 5px;
        width: 46px;
        height: 46px;
        border-radius: 50%;
        background: var(--ob-white);
        box-shadow: var(--ob-shadow-knob);
        transition: transform .35s cubic-bezier(.34, 1.4, .5, 1);
        transform: translateX(0);
    }
    .toggle-knob.corp { transform: translateX(56px); }

    .offer-band-label {
        margin-top: 20px;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: .16em;
        text-transform: uppercase;
        color: var(--eyebrow);
    }

    .offer-title-block { margin: 56px 0 44px; }

    @media (max-width: 700px) {
        .offer { padding: 56px 0 64px; }
        .offer-shell { padding: 0 20px; }
        .offer-toggle { gap: 18px; margin-top: 30px; }
        .toggle-track { width: 84px; height: 44px; }
        .toggle-knob { width: 34px; height: 34px; }
        .toggle-knob.corp { transform: translateX(40px); }
        .offer-title-block { margin: 40px 0 32px; }
    }
    .offer-h3 {
        margin: 0;
        font-size: clamp(28px, 3.6vw, 46px);
        font-weight: 800;
        letter-spacing: -.03em;
        line-height: 1.02;
        color: var(--ink);
    }
    .offer-blurb { margin: 16px 0 0; max-width: 520px; font-size: 16.5px; line-height: 1.6; color: var(--sub); }
</style>
